import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControl,
  Select,
  MenuItem,
  Stack,
  IconButton,
  Divider,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  CircularProgress,
  TextField,
  InputAdornment,
  Button,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import FolderCopyIcon from '@mui/icons-material/FolderCopy';
import AllInboxIcon from '@mui/icons-material/AllInbox';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CheckIcon from '@mui/icons-material/Check';
import ChecklistIcon from '@mui/icons-material/Checklist';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import ReplyIcon from '@mui/icons-material/Reply';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS } from '../helpers/constants';
import { getLoggedInUserData } from '../helpers/storageUtils';

/** Map API routine value to dropdown value (e.g. "24h", 1440, or "24 Hrs Interval" -> "24h") */
const ROUTINE_OPTIONS = ['15m', '30m', '45m', '1h', '4h', '8h', '12h', '24h'];
const ROUTINE_MINUTES_TO_VALUE: Record<number, string> = {
  15: '15m', 30: '30m', 45: '45m', 60: '1h', 240: '4h', 480: '8h', 720: '12h', 1440: '24h',
};
const normalizeRoutine = (routine: string | number | null | undefined): string => {
  if (routine == null) return '24h';
  if (typeof routine === 'number') return ROUTINE_MINUTES_TO_VALUE[routine] ?? '24h';
  const lower = String(routine).toLowerCase().replace(/\s/g, '');
  const match = ROUTINE_OPTIONS.find((opt) => lower.includes(opt.replace(/\s/g, '')) || opt === lower);
  return match ?? '24h';
};

/** Map dropdown value to API routine (minutes) */
const ROUTINE_TO_MINUTES: Record<string, number> = {
  '15m': 15,
  '30m': 30,
  '45m': 45,
  '1h': 60,
  '4h': 240,
  '8h': 480,
  '12h': 720,
  '24h': 1440,
};

export interface ChannelListItem {
  id: string;
  name: string;
  description?: string;
  workspace_id?: string;
  [key: string]: unknown;
}

export interface RealTimeNotificationSettingsResponse {
  id?: string;
  user_id?: string;
  workspace_id?: string;
  channel_id?: string;
  is_notifications_enabled?: boolean;
  apps_enabled?: string[];
  is_post_enabled?: boolean;
  is_mention_enabled?: boolean;
  is_task_enabled?: boolean;
  is_approval_enabled?: boolean;
  is_new_email_enabled?: boolean;
  is_email_reply_enabled?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ChannelRealtimeSettingsState {
  id?: string | null;
  workspace_id: string;
  muteAll: boolean;
  emailNotification: boolean;
  posts: boolean;
  approvals: boolean;
  tasks: boolean;
  mentions: boolean;
  replies: boolean;
}

export interface ChannelsListResponse {
  channels: ChannelListItem[];
  total: number;
  page: number;
  size: number;
}

export interface EmailNotifySettingsResponse {
  id?: string | null;
  resource_id?: string;
  routine?: string | number | null;
  is_notification_enabled?: number;
  post_enabled?: number;
  app_enabled?: number;
  blocker_enabled?: number;
  approval_pending_enabled?: number;
  meeting_enabled?: number;
  task_enabled?: number;
  mention_enabled?: number;
  reply_enabled?: number;
  chat_enabled?: number;
  friend_enabled?: number;
  created_by?: string | null;
  updated_by?: string | null;
  created_time?: string | null;
  updated_time?: string | null;
  last_email_notify_time?: string | null;
}

const SettingsPage: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const { isDarkMode } = useTheme();
  const muiTheme = useMuiTheme();
  const userId = useMemo(() => {
    const userData = getLoggedInUserData();
    return userData?.user_id ?? '';
  }, []);

  const [emailEnabled, setEmailEnabled] = useState(true);
  const [summaryInterval, setSummaryInterval] = useState('24h');
  const [postsEnabled, setPostsEnabled] = useState(true);
  const [approvalsEnabled, setApprovalsEnabled] = useState(false);
  const [tasksEnabled, setTasksEnabled] = useState(true);
  const [mentionsEnabled, setMentionsEnabled] = useState(true);
  const [repliesEnabled, setRepliesEnabled] = useState(true);
  const [muteAllNotifications, setMuteAllNotifications] = useState(false);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [emailNotificationExpanded, setEmailNotificationExpanded] = useState(true);
  const [channelDialogOpen, setChannelDialogOpen] = useState(false);
  const [expandedChannelId, setExpandedChannelId] = useState<string | null>(null);
  const [emailSettingsLoading, setEmailSettingsLoading] = useState(true);
  const [emailSettingsError, setEmailSettingsError] = useState<string | null>(null);
  const [emailSettingsId, setEmailSettingsId] = useState<string | null>(null);
  const [emailSettingsSaving, setEmailSettingsSaving] = useState(false);
  const [channelsList, setChannelsList] = useState<ChannelListItem[]>([]);
  const [channelsTotal, setChannelsTotal] = useState(0);
  const [channelsLoading, setChannelsLoading] = useState(true);
  const [channelsError, setChannelsError] = useState<string | null>(null);
  const [channelRealtimeSettings, setChannelRealtimeSettings] = useState<
    Record<string, ChannelRealtimeSettingsState>
  >({});
  const [channelSettingsLoading, setChannelSettingsLoading] = useState<Record<string, boolean>>({});
  const [channelSettingsSaving, setChannelSettingsSaving] = useState(false);
  const [channelSearchQuery, setChannelSearchQuery] = useState('');

  const CHANNELS_CARD_LIMIT = 8;

  // Mute/Unmute: use MUI palette.mode (matches ThemeProvider) + color="inherit" so outlinedPrimary overrides don’t force a light pill
  const muteToggleButtonSx = useMemo(() => {
    const dark = muiTheme.palette.mode === 'dark';
    return dark
      ? {
          textTransform: 'none' as const,
          color: muiTheme.palette.grey[100],
          borderColor: muiTheme.palette.divider,
          backgroundColor: muiTheme.palette.action.hover,
          '&:hover': {
            borderColor: muiTheme.palette.grey[500],
            backgroundColor: muiTheme.palette.action.selected,
          },
          '& .MuiButton-startIcon': { color: 'inherit' },
        }
      : {
          textTransform: 'none' as const,
          color: 'text.secondary',
          borderColor: '#E0E0E0',
          backgroundColor: '#F5F5F5',
          '&:hover': {
            borderColor: '#BDBDBD',
            backgroundColor: '#EEEEEE',
          },
          '& .MuiButton-startIcon': { color: 'inherit' },
        };
  }, [muiTheme]);

  useEffect(() => {
    if (!userId) {
      setEmailSettingsLoading(false);
      return;
    }
    const fetchEmailNotifySettings = async () => {
      setEmailSettingsLoading(true);
      setEmailSettingsError(null);
      try {
        const token =
          localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) ||
          localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) ||
          '';
        const url = `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.NOTIFICATIONS.EMAIL_NOTIFY_SETTINGS(userId)}`;
        const headers: Record<string, string> = { accept: 'application/json' };
        if (token) headers.Authorization = `Bearer ${token}`;
        const response = await fetch(url, { method: 'GET', headers });
        if (!response.ok) {
          const errText = await response.text();
          throw new Error(errText || `Failed to load email notification settings (${response.status})`);
        }
        const data: EmailNotifySettingsResponse = await response.json();
        const notifEnabled = data.is_notification_enabled === 1;
        setEmailEnabled(notifEnabled);
        setMuteAllNotifications(!notifEnabled);
        setSummaryInterval(normalizeRoutine(data.routine));
        setPostsEnabled((data.post_enabled ?? 1) === 1);
        setApprovalsEnabled((data.approval_pending_enabled ?? 0) === 1);
        setTasksEnabled((data.task_enabled ?? 1) === 1);
        setMentionsEnabled((data.mention_enabled ?? 1) === 1);
        setRepliesEnabled((data.reply_enabled ?? 1) === 1);
        setEmailSettingsId(data.id ? String(data.id) : null);
      } catch (err) {
        setEmailSettingsError(
          err instanceof Error ? err.message : 'Failed to load email notification settings'
        );
      } finally {
        setEmailSettingsLoading(false);
      }
    };
    fetchEmailNotifySettings();
  }, [userId]);

  useEffect(() => {
    const fetchChannels = async () => {
      setChannelsLoading(true);
      setChannelsError(null);
      try {
        const token =
          localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) ||
          localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) ||
          '';
        const params = new URLSearchParams({ page: '1', size: '100' });
        const url = `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.LIST}?${params}`;
        const headers: Record<string, string> = { accept: 'application/json' };
        if (token) headers.Authorization = `Bearer ${token}`;
        const response = await fetch(url, { method: 'GET', headers });
        if (!response.ok) {
          const errText = await response.text();
          throw new Error(errText || `Failed to load channels (${response.status})`);
        }
        const data: ChannelsListResponse = await response.json();
        setChannelsList(data.channels || []);
        setChannelsTotal(data.total ?? 0);
      } catch (err) {
        setChannelsError(
          err instanceof Error ? err.message : 'Failed to load channels'
        );
      } finally {
        setChannelsLoading(false);
      }
    };
    fetchChannels();
  }, []);

  const channelsForCard = channelsList.slice(0, CHANNELS_CARD_LIMIT);
  const channelsForModal = channelsList;
  const channelsFiltered = useMemo(() => {
    if (!channelSearchQuery.trim()) return channelsForModal;
    const q = channelSearchQuery.toLowerCase().trim();
    return channelsForModal.filter((c) => String(c.name || '').toLowerCase().includes(q));
  }, [channelsForModal, channelSearchQuery]);

  const getDefaultChannelSettings = useCallback(
    (channel: ChannelListItem): ChannelRealtimeSettingsState => ({
      workspace_id: (channel.workspace_id as string) || '',
      muteAll: false,
      emailNotification: true,
      posts: true,
      approvals: false,
      tasks: true,
      mentions: true,
      replies: true,
    }),
    []
  );

  useEffect(() => {
    if (!expandedChannelId || !userId) return;
    const channel = channelsForModal.find((c) => String(c.id) === expandedChannelId);
    if (!channel || channelRealtimeSettings[expandedChannelId]) return;

    const fetchChannelSettings = async () => {
      setChannelSettingsLoading((prev) => ({ ...prev, [expandedChannelId]: true }));
      try {
        const token =
          localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) ||
          localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) ||
          '';
        const url = `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.NOTIFICATIONS.REAL_TIME_SETTINGS(userId, expandedChannelId)}`;
        const headers: Record<string, string> = { accept: 'application/json' };
        if (token) headers.Authorization = `Bearer ${token}`;
        const response = await fetch(url, { method: 'GET', headers });
        if (!response.ok) {
          const errText = await response.text();
          throw new Error(errText || `Failed to load channel settings (${response.status})`);
        }
        const data: RealTimeNotificationSettingsResponse = await response.json();
        const muteAll = !(data.is_notifications_enabled ?? true);
        setChannelRealtimeSettings((prev) => ({
          ...prev,
          [expandedChannelId]: {
            id: data.id ?? null,
            workspace_id: data.workspace_id ?? (channel.workspace_id as string) ?? '',
            muteAll,
            emailNotification: data.is_new_email_enabled ?? true,
            posts: data.is_post_enabled ?? data.is_new_email_enabled ?? true,
            approvals: data.is_approval_enabled ?? false,
            tasks: data.is_task_enabled ?? true,
            mentions: data.is_mention_enabled ?? true,
            replies: data.is_email_reply_enabled ?? true,
          },
        }));
      } catch {
        setChannelRealtimeSettings((prev) => ({
          ...prev,
          [expandedChannelId]: getDefaultChannelSettings(channel),
        }));
      } finally {
        setChannelSettingsLoading((prev) => ({ ...prev, [expandedChannelId]: false }));
      }
    };
    fetchChannelSettings();
  }, [expandedChannelId, userId, channelsForModal, channelRealtimeSettings, getDefaultChannelSettings]);

  const updateChannelSetting = useCallback(
    (channelId: string, key: keyof ChannelRealtimeSettingsState, value: boolean) => {
      setChannelRealtimeSettings((prev) => {
        const current = prev[channelId];
        if (!current) return prev;
        return {
          ...prev,
          [channelId]: { ...current, [key]: value },
        };
      });
    },
    []
  );

  const handleSaveChannelNotifications = useCallback(async () => {
    if (!expandedChannelId || !userId) return;
    const settings = channelRealtimeSettings[expandedChannelId];
    const channel = channelsForModal.find((c) => String(c.id) === expandedChannelId);
    if (!settings || !channel) return;
    const workspaceId = settings.workspace_id || (channel.workspace_id as string);
    if (!workspaceId) {
      showError('Workspace is required to save channel notification settings.');
      return;
    }
    setChannelSettingsSaving(true);
    try {
      const token =
        localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) ||
        localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) ||
        '';
      const isUpdate = !!settings.id;
      const url = `${API_CONFIG.API_BASE_URL}${
        isUpdate ? API_ENDPOINTS.NOTIFICATIONS.UPDATE : API_ENDPOINTS.NOTIFICATIONS.SAVE
      }`;
      const method = isUpdate ? 'PUT' : 'POST';
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        accept: 'application/json',
      };
      if (token) headers.Authorization = `Bearer ${token}`;
      const body = {
        ...(isUpdate ? { id: settings.id } : {}),
        workspace_id: workspaceId,
        channel_id: expandedChannelId,
        is_notifications_enabled: !settings.muteAll,
        apps_enabled: [] as string[],
        is_post_enabled: settings.posts,
        is_mention_enabled: settings.mentions,
        is_task_enabled: settings.tasks,
        is_approval_enabled: settings.approvals,
        is_new_email_enabled: settings.emailNotification,
        is_email_reply_enabled: settings.replies,
      };
      const response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || `Failed to ${isUpdate ? 'update' : 'save'} (${response.status})`);
      }
      const data = (await response.json()) as RealTimeNotificationSettingsResponse;
      setChannelRealtimeSettings((prev) => ({
        ...prev,
        [expandedChannelId]: {
          ...settings,
          id: data.id ?? settings.id,
          workspace_id: data.workspace_id ?? workspaceId,
        },
      }));
      showSuccess(
        `Channel notification settings ${isUpdate ? 'updated' : 'saved'} successfully`
      );
      setChannelDialogOpen(false);
    } catch (err) {
      showError(
        err instanceof Error ? err.message : 'Failed to save channel notification settings'
      );
    } finally {
      setChannelSettingsSaving(false);
    }
  }, [
    expandedChannelId,
    userId,
    channelRealtimeSettings,
    channelsForModal,
    showSuccess,
    showError,
  ]);

  const handleSaveEmailNotifySettings = useCallback(
    async (options?: { closeModal?: boolean }) => {
      if (!userId) return;
      const { closeModal = false } = options ?? {};
      setEmailSettingsSaving(true);
      try {
        const token =
          localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) ||
          localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) ||
          '';
        const isUpdate = !!emailSettingsId;
        const endpoint = isUpdate
          ? API_ENDPOINTS.NOTIFICATIONS.EMAIL_NOTIFY_UPDATE
          : API_ENDPOINTS.NOTIFICATIONS.EMAIL_NOTIFY_SAVE;
        const url = `${API_CONFIG.API_BASE_URL}${endpoint}`;
        const method = isUpdate ? 'PUT' : 'POST';
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          accept: 'application/json',
        };
        if (token) headers.Authorization = `Bearer ${token}`;

        const routineMinutes = ROUTINE_TO_MINUTES[summaryInterval] ?? 1440;
        const isNotifEnabled = muteAllNotifications ? 0 : 1;
        const toNum = (v: boolean) => (v ? 1 : 0);

        const payload = {
          resource_id: userId,
          routine: routineMinutes,
          is_notification_enabled: isNotifEnabled,
          post_enabled: toNum(postsEnabled),
          app_enabled: 0,
          blocker_enabled: 0,
          approval_pending_enabled: toNum(approvalsEnabled),
          meeting_enabled: 0,
          task_enabled: toNum(tasksEnabled),
          mention_enabled: toNum(mentionsEnabled),
          reply_enabled: toNum(repliesEnabled),
          chat_enabled: 0,
          friend_enabled: 0,
        };
        const body = isUpdate ? { id: emailSettingsId, ...payload } : payload;

        const response = await fetch(url, {
          method,
          headers,
          body: JSON.stringify(body),
        });
        if (!response.ok) {
          const errText = await response.text();
          throw new Error(errText || `Failed to ${isUpdate ? 'update' : 'save'} (${response.status})`);
        }
        const data = (await response.json()) as EmailNotifySettingsResponse;
        if (data.id) setEmailSettingsId(String(data.id));
        setEmailEnabled(isNotifEnabled === 1);
        if (closeModal) setNotificationDialogOpen(false);
        showSuccess(
          `Email notification settings ${isUpdate ? 'updated' : 'saved'} successfully`
        );
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Failed to save email notification settings';
        showError(msg);
      } finally {
        setEmailSettingsSaving(false);
      }
    },
    [
      userId,
      emailSettingsId,
      summaryInterval,
      muteAllNotifications,
      postsEnabled,
      approvalsEnabled,
      tasksEnabled,
      mentionsEnabled,
      repliesEnabled,
      showSuccess,
      showError,
    ]
  );

  const saveRef = useRef(handleSaveEmailNotifySettings);
  saveRef.current = handleSaveEmailNotifySettings;
  const debouncedSave = useMemo(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    return () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => saveRef.current(), 500);
    };
  }, []);

  const handleCardEmailToggle = (checked: boolean) => {
    setEmailEnabled(checked);
    setMuteAllNotifications(!checked);
    debouncedSave();
  };

  const handleSummaryIntervalChange = (value: string) => {
    setSummaryInterval(value);
    debouncedSave();
  };

  const handleCardPostsToggle = (checked: boolean) => {
    setPostsEnabled(checked);
    debouncedSave();
  };

  const handleCardApprovalsToggle = (checked: boolean) => {
    setApprovalsEnabled(checked);
    debouncedSave();
  };

  const handleModalMuteToggle = (checked: boolean) => {
    setMuteAllNotifications(checked);
    setEmailEnabled(!checked);
    debouncedSave();
  };

  const handleModalPostsToggle = (checked: boolean) => {
    setPostsEnabled(checked);
    debouncedSave();
  };

  const handleModalApprovalsToggle = (checked: boolean) => {
    setApprovalsEnabled(checked);
    debouncedSave();
  };

  const handleModalTasksToggle = (checked: boolean) => {
    setTasksEnabled(checked);
    debouncedSave();
  };

  const handleModalMentionsToggle = (checked: boolean) => {
    setMentionsEnabled(checked);
    debouncedSave();
  };

  const handleModalRepliesToggle = (checked: boolean) => {
    setRepliesEnabled(checked);
    debouncedSave();
  };

  const NotificationSwitch = ({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) => (
    <Switch
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
      size="small"
      color="primary"
    />
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3,
            alignItems: 'stretch',
          }}
        >
        {/* Email Notifications Card */}
        <Box sx={{ flex: '1 1 360px', minWidth: 0, display: 'flex' }}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 2,
              flex: 1,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Header with icon, title and toggle */}
            <Stack direction="row" alignItems="center" spacing={2} mb={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <EmailIcon
                  fontSize="small"
                  sx={{
                    color: '#00a7ff',
                    fontSize: '24px', // slightly larger (about +1px)
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#00a7ff',
                  }}
                >
                  Email Notifications
                </Typography>
              </Stack>
              <Switch
                checked={emailEnabled}
                onChange={(event) => handleCardEmailToggle(event.target.checked)}
                color="primary"
                disabled={emailSettingsLoading}
                sx={{
                  ml: 0.5,
                }}
              />
            </Stack>

            <Typography variant="body2" color="text.secondary" mb={3}>
              Outgoing alert notifications from platform
            </Typography>

            {emailSettingsLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {emailSettingsError && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                {emailSettingsError}
              </Typography>
            )}
            {!emailSettingsLoading && (
            <>
            {/* Summary mail row with dropdown */}
            <Stack
              direction="row"
              alignItems="center"
              mb={2}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, mr: 0.5 }}
                >
                  Summary Mail
                </Typography>
                <FormControl size="small" sx={{ minWidth: 115, maxWidth: 135 }}>
                  <Select
                    value={summaryInterval}
                    onChange={(event) => handleSummaryIntervalChange(event.target.value as string)}
                    disabled={!emailEnabled}
                    size="small"
                    sx={{
                      minWidth: 120,
                      maxWidth: 135,
                      height: 28,
                      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa',
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      '& .MuiSelect-select': {
                        py: 0.25,
                        px: 0.75,
                        fontSize: '0.75rem',
                      },
                      '& fieldset': {
                        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.23)' : '#b2b2b2',
                      },
                      '&:hover fieldset': {
                        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.23)' : '#b2b2b2',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#00a7ff',
                        borderWidth: '1px',
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: isDarkMode ? 'grey.800' : '#f5f5f5',
                          border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #e0e0e0',
                          boxShadow: isDarkMode ? '0px 4px 12px rgba(0, 0, 0, 0.4)' : '0px 4px 12px rgba(0, 0, 0, 0.12)',
                          mt: 0.5,
                          minWidth: 'auto',
                          '& .MuiMenuItem-root': {
                            fontSize: '0.75rem',
                            minHeight: 28,
                            py: 0.5,
                            justifyContent: 'center',
                            textAlign: 'center',
                            color: isDarkMode ? 'inherit' : 'inherit',
                            '&.Mui-selected': {
                              backgroundColor: '#00a7ff',
                              color: '#fff',
                              '&:hover': {
                                backgroundColor: '#00a7ff',
                                color: '#fff',
                              },
                            },
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value="15m">15 Mins Interval</MenuItem>
                    <MenuItem value="30m">30 Mins Interval</MenuItem>
                    <MenuItem value="45m">45 Mins Interval</MenuItem>
                    <MenuItem value="1h">1 Hr Interval</MenuItem>
                    <MenuItem value="4h">4 Hrs Interval</MenuItem>
                    <MenuItem value="8h">8 Hrs Interval</MenuItem>
                    <MenuItem value="12h">12 Hrs Interval</MenuItem>
                    <MenuItem value="24h">24 Hrs Interval</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Stack>

            <Divider sx={{ mb: 1.5 }} />

            {/* Mute all Notification row */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1.5,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>Mute all Notification</Typography>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                onClick={() => handleModalMuteToggle(!muteAllNotifications)}
                startIcon={muteAllNotifications ? <NotificationsOffIcon /> : <NotificationsIcon />}
                sx={muteToggleButtonSx}
              >
                {muteAllNotifications ? 'Unmute' : 'Mute'}
              </Button>
            </Box>

            <Divider sx={{ mb: 1.5 }} />

            {/* Email Notification types - same format as modal */}
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              {[
                { icon: DescriptionOutlinedIcon, label: 'Internal Message', checked: postsEnabled, onChange: handleCardPostsToggle },
                { icon: CheckIcon, label: 'Approvals', checked: approvalsEnabled, onChange: handleCardApprovalsToggle },
                { icon: ChecklistIcon, label: 'Tasks', checked: tasksEnabled, onChange: handleModalTasksToggle },
                { icon: AlternateEmailIcon, label: 'Mentions', checked: mentionsEnabled, onChange: handleModalMentionsToggle },
                { icon: ReplyIcon, label: 'Replies', checked: repliesEnabled, onChange: handleModalRepliesToggle },
              ].map(({ icon: Icon, label, checked, onChange }) => (
                <Stack key={label} direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Icon fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Typography variant="body2">{label}</Typography>
                  </Stack>
                  <NotificationSwitch
                    checked={!muteAllNotifications && checked}
                    onChange={onChange}
                    disabled={!emailEnabled || muteAllNotifications}
                  />
                </Stack>
              ))}
            </Stack>
            </>
            )}
          </Paper>
        </Box>

        {/* Channel Notifications Card */}
        <Box sx={{ flex: '1 1 360px', minWidth: 0, display: 'flex' }}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 2,
              flex: 1,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AllInboxIcon
                  fontSize="small"
                  sx={{
                    color: '#00a7ff',
                    fontSize: '21px',
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#00a7ff',
                  }}
                >
                  Channel Notifications
                </Typography>
              </Stack>
              <Link
                component="button"
                type="button"
                underline="hover"
                sx={{ fontSize: '0.875rem', color: '#00a7ff' }}
                onClick={() => setChannelDialogOpen(true)}
              >
                View all
              </Link>
            </Stack>

            <Typography variant="body2" color="text.secondary" mb={2}>
              Channel list ({channelsForCard.length} of {channelsTotal})
            </Typography>

            {channelsLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {channelsError && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                {channelsError}
              </Typography>
            )}
            {!channelsLoading &&
            channelsForCard.map((channel) => (
              <Box
                key={channel.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1,
                }}
              >
                <Typography variant="body2">{channel.name}</Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    setExpandedChannelId(String(channel.id));
                    setChannelDialogOpen(true);
                  }}
                  aria-label={`Edit ${channel.name} notifications`}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Paper>
        </Box>
        </Box>
      </Box>

      {/* Notification Settings Modal (Email) */}
      <Dialog
        open={notificationDialogOpen}
        onClose={() => setNotificationDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            maxWidth: 750, // slightly wider (~20-30px more)
            border: '1px solid rgba(0, 0, 0, 0.12)',
            borderRadius: 1,
            boxShadow: '0px 11px 15px -7px rgba(0,0,0,0.2), 0px 24px 38px 3px rgba(0,0,0,0.14), 0px 9px 46px 8px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
          }}
        >
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            Notification Settings
          </Typography>
          <IconButton size="small" onClick={() => setNotificationDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent
          sx={{
            pt: 2,
            pb: 0,
            px: 3,
          }}
        >
          {/* Mute all Notification row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1.5,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Mute all Notification</Typography>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              onClick={() => handleModalMuteToggle(!muteAllNotifications)}
              startIcon={muteAllNotifications ? <NotificationsOffIcon /> : <NotificationsIcon />}
              sx={muteToggleButtonSx}
            >
              {muteAllNotifications ? 'Unmute' : 'Mute'}
            </Button>
          </Box>
          <Divider sx={{ mx: -3 }} />

          {/* Email Notification section */}
          <Box sx={{ mt: 2 }}>
            <Box
              onClick={() => setEmailNotificationExpanded(!emailNotificationExpanded)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                mb: emailNotificationExpanded ? 1 : 0,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, fontSize: '0.9rem' }}
              >
                Email Notification
              </Typography>
              <ExpandMoreIcon
                fontSize="small"
                sx={{
                  color: 'text.secondary',
                  transform: emailNotificationExpanded ? 'rotate(180deg)' : 'none',
                  transition: 'transform 150ms ease',
                }}
              />
            </Box>

            <Collapse in={emailNotificationExpanded}>
              <Box sx={{ pl: 0 }}>
                {[
                  { key: 'posts', icon: DescriptionOutlinedIcon, label: 'Internal Message', checked: postsEnabled, onChange: handleModalPostsToggle },
                  { key: 'approvals', icon: CheckIcon, label: 'Approvals', checked: approvalsEnabled, onChange: handleModalApprovalsToggle },
                  { key: 'tasks', icon: ChecklistIcon, label: 'Tasks', checked: tasksEnabled, onChange: handleModalTasksToggle },
                  { key: 'mentions', icon: AlternateEmailIcon, label: 'Mentions', checked: mentionsEnabled, onChange: handleModalMentionsToggle },
                  { key: 'replies', icon: ReplyIcon, label: 'Replies', checked: repliesEnabled, onChange: handleModalRepliesToggle },
                ].map(({ key, icon: Icon, label, checked, onChange }) => (
                  <Box
                    key={key}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 1.25,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Icon fontSize="small" sx={{ color: 'text.secondary', fontSize: 24 }} />
                      <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.9rem' }}>
                        {label}
                      </Typography>
                    </Box>
                    <NotificationSwitch
                      checked={!muteAllNotifications && checked}
                      onChange={onChange}
                      disabled={muteAllNotifications}
                    />
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Box>
        </DialogContent>
        <Divider sx={{ my: '1px' }} />
        <DialogActions sx={{ px: 3, py: 1.5, pb: 2 }}>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton
            sx={{
              px: 3,
              py: 1,
              borderRadius: 1,
              bgcolor: 'primary.main',
              color: '#fff',
              '&:hover': { bgcolor: 'primary.dark', color: '#fff' },
            }}
            onClick={() => handleSaveEmailNotifySettings({ closeModal: true })}
            disabled={emailSettingsSaving}
          >
            <Typography variant="button" sx={{ fontSize: '0.8rem', textTransform: 'none', color: '#fff' }}>
              {emailSettingsSaving ? 'Saving...' : 'Save'}
            </Typography>
          </IconButton>
        </DialogActions>
      </Dialog>

      {/* Channel Notifications Modal */}
      <Dialog
        open={channelDialogOpen}
        onClose={() => { setChannelDialogOpen(false); setExpandedChannelId(null); setChannelSearchQuery(''); }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            maxWidth: 520,
            border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
            borderRadius: 1,
            boxShadow: isDarkMode ? '0px 11px 15px -7px rgba(0,0,0,0.4), 0px 24px 38px 3px rgba(0,0,0,0.35), 0px 9px 46px 8px rgba(0,0,0,0.3)' : '0px 11px 15px -7px rgba(0,0,0,0.2), 0px 24px 38px 3px rgba(0,0,0,0.14), 0px 9px 46px 8px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <AllInboxIcon sx={{ color: '#00a7ff', fontSize: 24 }} />
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
              Channel Notifications
            </Typography>
          </Stack>
          <IconButton size="small" onClick={() => { setChannelDialogOpen(false); setExpandedChannelId(null); setChannelSearchQuery(''); }} sx={{ color: '#d32f2f', '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.04)' } }} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Box sx={{ px: 3, pb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search channels by name"
            value={channelSearchQuery}
            onChange={(e) => setChannelSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f5f5f5',
                '& fieldset': { borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.12)' },
                '&:hover fieldset': { borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)' },
              },
            }}
          />
        </Box>
        <Divider />
        <DialogContent
          sx={{
            pt: 2,
            pb: 0,
            px: 0,
            minHeight: 220,
            overflow: 'auto',
          }}
        >
          {channelsFiltered.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No channels match your search.
              </Typography>
            </Box>
          ) : (
          channelsFiltered.map((channel, index) => {
            const channelKey = String(channel.id);
            const isExpanded = expandedChannelId === channelKey;
            return (
              <Box key={channel.id} sx={{ mb: 1.5, mx: 2 }}>
                {/* Channel row header - white/dark when collapsed, blue when expanded */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.25,
                    borderRadius: isExpanded ? '8px 8px 0 0' : '8px',
                    backgroundColor: isExpanded ? '#00a7ff' : (isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#fff'),
                    border: isExpanded ? 'none' : '1px solid',
                    borderColor: isExpanded ? 'transparent' : 'divider',
                    cursor: 'pointer',
                  }}
                  onClick={() =>
                    setExpandedChannelId(isExpanded ? null : channelKey)
                  }
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <AllInboxIcon sx={{ color: isExpanded ? '#fff' : 'text.secondary', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: isExpanded ? '#fff' : 'text.primary' }}>
                      {channel.name}
                    </Typography>
                  </Stack>
                  <ExpandMoreIcon
                    sx={{
                      color: isExpanded ? '#fff' : 'text.secondary',
                      fontSize: 20,
                      transform: isExpanded ? 'rotate(180deg)' : 'none',
                      transition: 'transform 150ms ease',
                    }}
                  />
                </Box>

                {/* Expanded channel notification settings - grey background */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box
                    sx={(theme) => ({
                      px: 2,
                      pt: 1.5,
                      pb: 1.5,
                      border: `1px solid ${theme.palette.divider}`,
                      borderTop: 'none',
                      borderRadius: '0 0 8px 8px',
                      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f5f5f5',
                    })}
                  >
                    {channelSettingsLoading[channelKey] ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      (() => {
                        const s = channelRealtimeSettings[channelKey] ?? getDefaultChannelSettings(channel);
                        return (
                          <>
                            {/* Real Time Notification header with Mute */}
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 1.5,
                                px: 1.5,
                                py: 1,
                                borderRadius: 1,
                                backgroundColor: isDarkMode ? 'rgba(0, 167, 255, 0.12)' : 'rgba(0, 167, 255, 0.08)',
                                border: '1px solid',
                                borderColor: isDarkMode ? 'rgba(0, 167, 255, 0.25)' : 'rgba(0, 167, 255, 0.15)',
                              }}
                            >
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <NotificationsIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                  Real Time Notification
                                </Typography>
                              </Stack>
                              <Button
                                variant="outlined"
                                color="inherit"
                                size="small"
                                onClick={() => updateChannelSetting(channelKey, 'muteAll', !s.muteAll)}
                                startIcon={s.muteAll ? <NotificationsOffIcon /> : <NotificationsIcon />}
                                sx={muteToggleButtonSx}
                              >
                                {s.muteAll ? 'Unmute' : 'Mute'}
                              </Button>
                            </Box>

                            <List dense disablePadding>
                              <ListItem
                                sx={{ py: 0.5 }}
                                secondaryAction={
                                  <Switch
                                    edge="end"
                                    checked={!s.muteAll && s.posts}
                                    onChange={(e) =>
                                      updateChannelSetting(channelKey, 'posts', e.target.checked)
                                    }
                                    disabled={s.muteAll}
                                    size="small"
                                    color="primary"
                                  />
                                }
                              >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <DescriptionOutlinedIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                  primary="Internal Message"
                                  primaryTypographyProps={{ fontSize: '0.9rem' }}
                                />
                              </ListItem>

                              <ListItem
                                sx={{ py: 0.5 }}
                                secondaryAction={
                                  <Switch
                                    edge="end"
                                    checked={!s.muteAll && s.approvals}
                                    onChange={(e) =>
                                      updateChannelSetting(channelKey, 'approvals', e.target.checked)
                                    }
                                    disabled={s.muteAll}
                                    size="small"
                                    color="primary"
                                  />
                                }
                              >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <CheckIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                  primary="Approvals"
                                  primaryTypographyProps={{ fontSize: '0.9rem' }}
                                />
                              </ListItem>

                              <ListItem
                                sx={{ py: 0.5 }}
                                secondaryAction={
                                  <Switch
                                    edge="end"
                                    checked={!s.muteAll && s.tasks}
                                    onChange={(e) =>
                                      updateChannelSetting(channelKey, 'tasks', e.target.checked)
                                    }
                                    disabled={s.muteAll}
                                    size="small"
                                    color="primary"
                                  />
                                }
                              >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <ChecklistIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                  primary="Tasks"
                                  primaryTypographyProps={{ fontSize: '0.9rem' }}
                                />
                              </ListItem>

                              <ListItem
                                sx={{ py: 0.5 }}
                                secondaryAction={
                                  <Switch
                                    edge="end"
                                    checked={!s.muteAll && s.mentions}
                                    onChange={(e) =>
                                      updateChannelSetting(channelKey, 'mentions', e.target.checked)
                                    }
                                    disabled={s.muteAll}
                                    size="small"
                                    color="primary"
                                  />
                                }
                              >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <AlternateEmailIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                  primary="Mentions"
                                  primaryTypographyProps={{ fontSize: '0.9rem' }}
                                />
                              </ListItem>

                              <ListItem
                                sx={{ py: 0.5 }}
                                secondaryAction={
                                  <Switch
                                    edge="end"
                                    checked={!s.muteAll && s.replies}
                                    onChange={(e) =>
                                      updateChannelSetting(channelKey, 'replies', e.target.checked)
                                    }
                                    disabled={s.muteAll}
                                    size="small"
                                    color="primary"
                                  />
                                }
                              >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <ReplyIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                  primary="Replies"
                                  primaryTypographyProps={{ fontSize: '0.9rem' }}
                                />
                              </ListItem>

                              <ListItem
                                sx={{ py: 0.5 }}
                                secondaryAction={
                                  <Switch
                                    edge="end"
                                    checked={!s.muteAll && s.emailNotification}
                                    onChange={(e) =>
                                      updateChannelSetting(channelKey, 'emailNotification', e.target.checked)
                                    }
                                    disabled={s.muteAll}
                                    size="small"
                                    color="primary"
                                  />
                                }
                              >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <EmailIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                  primary="Email"
                                  primaryTypographyProps={{ fontSize: '0.9rem' }}
                                />
                              </ListItem>
                            </List>
                          </>
                        );
                      })()
                    )}
                  </Box>
                </Collapse>
              </Box>
            );
          })
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, pt: 1, pb: 1 }}>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton
            sx={{
              px: 3,
              py: 1,
              borderRadius: 1,
              bgcolor: 'primary.main',
              color: '#fff',
              '&:hover': { bgcolor: 'primary.dark', color: '#fff' },
            }}
            onClick={handleSaveChannelNotifications}
            disabled={
              !expandedChannelId ||
              channelSettingsSaving ||
              !!channelSettingsLoading[expandedChannelId || '']
            }
          >
            <Typography variant="button" sx={{ fontSize: '0.8rem', textTransform: 'none', color: '#fff' }}>
              {channelSettingsSaving ? 'Saving...' : 'Save'}
            </Typography>
          </IconButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPage;

